import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  Switch,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useOldAutomation } from './oldAutomation';
import type { OldItem } from './oldAutomation';
import {
  DayToggles,
  SettingRow,
  StatusPill,
  UI,
  denseInputSx,
  primaryButtonSx,
  textButtonSx,
} from './settingsUi';
import type { PillTone } from './settingsUi';
import {
  BUSY_NOTE_MAX_MINUTES,
  BUSY_NOTE_MIN_MINUTES,
  DAY_KEYS,
  DEFAULT_AUTO_REPLY_SETTINGS,
  WHATSAPP_AUTO_REPLY_DOC,
  WHATSAPP_SETTINGS_COLLECTION,
  normalizeAutoReplySettings,
  resolveWorkingMode,
  toPhone10,
  validateAutoReplySettings,
  zonedParts,
} from '../../utils/whatsappWorkingHours';
import type {
  ReplyLanguage,
  ResolvedMode,
  WhatsappAutoReplySettings,
  WhatsappHoliday,
  WorkingDayKey,
} from '../../utils/whatsappWorkingHours';

const DAY_SHORT: Record<WorkingDayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const STATUS: Record<ResolvedMode, { label: string; tone: PillTone }> = {
  office: { label: 'Open now', tone: 'green' },
  'after-hours': { label: 'Team is off now', tone: 'slate' },
};

const checkboxSx = { p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 }, '&.Mui-checked': { color: UI.gold } };
const radioSx = { p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 }, '&.Mui-checked': { color: UI.gold } };
const controlLabelSx = { m: 0, gap: 0.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: 13, color: UI.ink, pt: 0.4, lineHeight: 1.45 } };

const newId = () => `h_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const formatYmd = (ymd: string) => {
  const date = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return ymd;
  return date.toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const TimeRange = ({
  from,
  to,
  disabled,
  onChange,
}: {
  from: string;
  to: string;
  disabled?: boolean;
  onChange: (patch: { from?: string; to?: string }) => void;
}) => (
  <Stack direction="row" alignItems="center" gap={1}>
    <TextField size="small" type="time" value={from} disabled={disabled} onChange={(e) => onChange({ from: e.target.value })} sx={{ ...denseInputSx, width: 118 }} inputProps={{ 'aria-label': 'Opens at' }} />
    <Typography sx={{ fontSize: 12.5, color: UI.muted }}>to</Typography>
    <TextField size="small" type="time" value={to} disabled={disabled} onChange={(e) => onChange({ to: e.target.value })} sx={{ ...denseInputSx, width: 118 }} inputProps={{ 'aria-label': 'Closes at' }} />
  </Stack>
);

export const WorkingHoursSection = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<WhatsappAutoReplySettings>(DEFAULT_AUTO_REPLY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const old = useOldAutomation();
  const [savedMode, setSavedMode] = useState<string>('off');
  /** Go-live: overlapping older automation to turn off (checked) or keep. */
  const [goLive, setGoLive] = useState<Array<{ item: OldItem; replacedBy: string[]; checked: boolean }> | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newTestNumber, setNewTestNumber] = useState('');

  useEffect(() => {
    let active = true;
    getDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC))
      .then((snap) => {
        if (active && snap.exists()) {
          const loaded = normalizeAutoReplySettings(snap.data());
          setSettings(loaded);
          setSavedMode(loaded.autoReplyMode);
        }
      })
      .catch((err) => active && setNotice({ type: 'error', text: `Failed to load settings: ${err?.message || err}` }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const update = (patch: Partial<WhatsappAutoReplySettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setNotice(null);
  };

  const setPaused = async (paused: boolean) => {
    const restoredMode = settings.autoReplyModeBeforePause || settings.autoReplyMode;
    const legacyMode = paused ? 'off' : restoredMode;
    const next = {
      automationPaused: paused,
      autoReplyMode: legacyMode,
      autoReplyModeBeforePause: paused ? settings.autoReplyMode : null,
      enabled: legacyMode !== 'off',
    } as const;
    setSettings((prev) => ({ ...prev, ...next }));
    try {
      await setDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC), {
        ...next,
        updatedAt: serverTimestamp(),
        updatedByEmail: currentUser?.email || null,
      }, { merge: true });
      setNotice({ type: 'success', text: paused ? 'All automatic WhatsApp messages paused.' : 'Automatic WhatsApp messages resumed according to each flow mode.' });
    } catch (err: any) {
      setSettings((prev) => ({ ...prev, automationPaused: !paused, autoReplyMode: settings.autoReplyMode,
        autoReplyModeBeforePause: settings.autoReplyModeBeforePause, enabled: settings.enabled }));
      setNotice({ type: 'error', text: `Could not update pause control: ${err?.message || err}` });
    }
  };

  // Office and site are open together: open days share one time range.
  const officeDays = DAY_KEYS.filter((k) => settings.weekly[k].mode === 'office');
  const officeTimes = officeDays.length ? settings.weekly[officeDays[0]] : DEFAULT_AUTO_REPLY_SETTINGS.weekly.mon;

  const toggleDay = (key: WorkingDayKey) => {
    const weekly = { ...settings.weekly };
    weekly[key] = weekly[key].mode === 'office' ?
      { ...weekly[key], mode: 'closed' } :
      { mode: 'office', from: officeTimes.from, to: officeTimes.to };
    update({ weekly });
  };

  const setOpenTimes = (patch: { from?: string; to?: string }) => {
    const weekly = { ...settings.weekly };
    DAY_KEYS.forEach((k) => {
      if (weekly[k].mode === 'office') weekly[k] = { ...weekly[k], ...patch };
    });
    update({ weekly });
  };

  const todayYmd = zonedParts(now, settings.timezone).ymd;
  const upcomingHolidays = settings.holidays
    .filter((h) => h.toDate >= todayYmd)
    .sort((a, b) => a.fromDate.localeCompare(b.fromDate));

  const addHoliday = () => {
    const name = newHolidayName.trim();
    if (!newHolidayDate || !name) {
      setNotice({ type: 'error', text: 'Pick a date and enter a name for the holiday.' });
      return;
    }
    if (newHolidayDate < todayYmd) {
      setNotice({ type: 'error', text: 'That date is in the past.' });
      return;
    }
    if (settings.holidays.some((h) => newHolidayDate >= h.fromDate && newHolidayDate <= h.toDate)) {
      setNotice({ type: 'error', text: `${formatYmd(newHolidayDate)} is already a holiday.` });
      return;
    }
    const holiday: WhatsappHoliday = { id: newId(), name, fromDate: newHolidayDate, toDate: newHolidayDate, mode: 'closed', from: null, to: null };
    update({ holidays: [...settings.holidays, holiday] });
    setNewHolidayDate('');
    setNewHolidayName('');
  };

  const addTestNumber = () => {
    const phone10 = toPhone10(newTestNumber);
    if (!phone10) {
      setNotice({ type: 'error', text: 'Enter a 10-digit mobile number.' });
      return;
    }
    if (!settings.testNumbers.includes(phone10)) update({ testNumbers: [...settings.testNumbers, phone10] });
    setNewTestNumber('');
  };

  const removeHoliday = (id: string) => update({ holidays: settings.holidays.filter((h) => h.id !== id) });

  const persist = async (turnOff: OldItem[]) => {
    setSaving(true);
    try {
      // Drop holidays that have already passed.
      const holidays = settings.holidays
        .filter((h) => h.toDate >= todayYmd)
        .sort((a, b) => a.fromDate.localeCompare(b.fromDate));
      const next = { ...settings, holidays, enabled: settings.autoReplyMode !== 'off' };
      // Flow switches are saved instantly from the messages tab; never overwrite them here.
      const { flowEnabled: _flowEnabled, flowModes: _flowModes, ...toSave } = next;
      await setDoc(
        doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC),
        {
          ...toSave,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null,
          updatedByEmail: currentUser?.email || null,
        },
        { merge: true }
      );
      await Promise.all(turnOff.map((item) => old.setActive(item, false)));
      setSettings(next);
      setSavedMode(next.autoReplyMode);
      setDirty(false);
      setNotice({
        type: 'success',
        text: turnOff.length ? `Saved. Turned off older automation: ${turnOff.map((i) => i.name).join(', ')}.` : 'Saved.',
      });
    } catch (err: any) {
      setNotice({ type: 'error', text: `Save failed: ${err?.message || err}` });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const found = validateAutoReplySettings(settings);
    setErrors(found);
    if (found.length) return;
    // Going Live: offer to switch off older automation that the new flows replace.
    if (settings.autoReplyMode === 'live' && savedMode !== 'live') {
      const fresh = await getDoc(doc(db, WHATSAPP_SETTINGS_COLLECTION, WHATSAPP_AUTO_REPLY_DOC)).catch(() => null);
      const current = fresh?.exists() ? normalizeAutoReplySettings(fresh.data()) : settings;
      const rows = old.goLiveCandidates(current).map(({ item, replacedBy }) => ({
        item,
        replacedBy: replacedBy.map((a) => a.title),
        // Rules that message brokers stay on unless you tick them.
        checked: !/broker/i.test(item.recipient),
      }));
      if (rows.length) {
        setGoLive(rows);
        return;
      }
    }
    await persist([]);
  };

  const current = useMemo(() => resolveWorkingMode(settings, now), [settings, now]);
  const status = STATUS[current.mode];

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={22} sx={{ color: UI.gold }} />
      </Stack>
    );
  }

  return (
    <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5 }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${UI.line}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: UI.ink }}>Working hours</Typography>
          <Typography sx={{ fontSize: 12, color: UI.muted }}>India time. Decides which auto-reply a customer gets.</Typography>
        </Box>
        {settings.automationPaused && <StatusPill tone="amber">Automation paused</StatusPill>}
        <Tooltip title={current.reason}>
          <Box>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </Box>
        </Tooltip>
      </Stack>

      <Box sx={{ px: 2.5 }}>
        <SettingRow
          label="Automation safety"
          hint="Set Off, Test or Live on each journey and conversation flow. STOP always opts a contact out."
        >
          <Stack gap={1.25}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Switch checked={settings.automationPaused} onChange={(e) => setPaused(e.target.checked)}
                inputProps={{ 'aria-label': 'Pause all WhatsApp automation' }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: UI.gold }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: UI.gold } }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.ink }}>Pause all WhatsApp automation</Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: UI.muted }}>When paused, no automatic journeys or conversation replies send. Manual test sends remain available.</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.ink }}>Test numbers</Typography>
            <Typography sx={{ fontSize: 12, color: UI.muted }}>Flows in Test send only to these numbers.</Typography>
              <Stack gap={0.75}>
                {settings.testNumbers.length > 0 && (
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {settings.testNumbers.map((n) => (
                      <Stack key={n} direction="row" alignItems="center" sx={{ border: `1px solid ${UI.line}`, borderRadius: 999, pl: 1, pr: 0.25, height: 26, bgcolor: UI.canvas }}>
                        <Typography sx={{ fontSize: 12.5, color: UI.ink, fontVariantNumeric: 'tabular-nums' }}>+91 {n}</Typography>
                        <IconButton size="small" aria-label={`Remove ${n}`} onClick={() => update({ testNumbers: settings.testNumbers.filter((x) => x !== n) })} sx={{ p: 0.25, color: UI.faint }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
                <Stack direction="row" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Test number, e.g. 9876543210"
                    value={newTestNumber}
                    onChange={(e) => setNewTestNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addTestNumber();
                    }}
                    inputProps={{ 'aria-label': 'Test number', inputMode: 'tel' }}
                    sx={{ ...denseInputSx, width: 220 }}
                  />
                  <Button variant="outlined" onClick={addTestNumber} sx={textButtonSx}>
                    Add
                  </Button>
                </Stack>
              </Stack>
          </Stack>
        </SettingRow>

        <SettingRow label="Office & site hours" hint="Office and sites open together. A sales executive answers; the busy note covers slow replies. Outside these hours customers get the after-hours reply.">
          <Stack gap={1.25}>
            <DayToggles
              days={DAY_KEYS}
              labels={DAY_SHORT}
              isOn={(d) => settings.weekly[d].mode === 'office'}
              onToggle={(d) => toggleDay(d)}
            />
            <TimeRange from={officeTimes.from} to={officeTimes.to} disabled={!officeDays.length} onChange={(p) => setOpenTimes(p)} />
          </Stack>
        </SettingRow>

        <SettingRow label="Holidays" hint="Closed all day. The reply names the next working day.">
          <Stack gap={1}>
            {upcomingHolidays.length > 0 && (
              <Stack sx={{ border: `1px solid ${UI.line}`, borderRadius: 1.5 }}>
                {upcomingHolidays.map((h, idx) => (
                  <Stack
                    key={h.id}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ pl: 1.25, pr: 0.5, py: 0.25, borderTop: idx ? `1px solid ${UI.line}` : 'none' }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: UI.muted, width: 128, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {formatYmd(h.fromDate)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: UI.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.name}
                    </Typography>
                    <IconButton size="small" aria-label={`Remove ${h.name}`} onClick={() => removeHoliday(h.id)} sx={{ color: UI.faint, '&:hover': { color: UI.ink } }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap">
              <TextField
                size="small"
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                inputProps={{ min: todayYmd, 'aria-label': 'Holiday date' }}
                sx={{ ...denseInputSx, width: 150 }}
              />
              <TextField
                size="small"
                placeholder="Name, e.g. Diwali"
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addHoliday();
                }}
                inputProps={{ 'aria-label': 'Holiday name' }}
                sx={{ ...denseInputSx, flex: 1, minWidth: 140, maxWidth: 240 }}
              />
              <Button variant="outlined" onClick={addHoliday} sx={textButtonSx}>
                Add
              </Button>
            </Stack>
          </Stack>
        </SettingRow>

        <SettingRow label="Reply language" hint="A language saved on the lead always wins.">
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
            <TextField
              select
              size="small"
              value={settings.replyLanguage}
              onChange={(e) => update({ replyLanguage: e.target.value as ReplyLanguage })}
              inputProps={{ 'aria-label': 'Default reply language' }}
              sx={{ ...denseInputSx, width: 130 }}
            >
              <MenuItem value="en" sx={{ fontSize: 13 }}>English</MenuItem>
              <MenuItem value="gu" sx={{ fontSize: 13 }}>ગુજરાતી</MenuItem>
              <MenuItem value="hi" sx={{ fontSize: 13 }}>हिंदी</MenuItem>
            </TextField>
            <FormControlLabel
              sx={{ ...controlLabelSx, alignItems: 'center', '& .MuiFormControlLabel-label': { fontSize: 13, color: UI.ink } }}
              control={<Checkbox size="small" sx={checkboxSx} checked={settings.matchCustomerScript} onChange={(e) => update({ matchCustomerScript: e.target.checked })} />}
              label="Match the customer's script (Gujarati / Hindi)"
            />
          </Stack>
        </SettingRow>

        <SettingRow label="Busy note wait" hint="How long office-hours chats wait before the busy note. Turn the busy note on in Auto-reply messages." last>
          <Stack gap={0.75}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
              <FormControlLabel
                sx={{ ...controlLabelSx, alignItems: 'center', '& .MuiFormControlLabel-label': { fontSize: 12.5, color: UI.ink } }}
                control={<Radio size="small" sx={radioSx} checked={settings.busyNoteWaitMode === 'learn'} onChange={() => update({ busyNoteWaitMode: 'learn' })} />}
                label="Learn from our reply times"
              />
              <FormControlLabel
                sx={{ ...controlLabelSx, alignItems: 'center', ml: 1, '& .MuiFormControlLabel-label': { fontSize: 12.5, color: UI.ink } }}
                control={<Radio size="small" sx={radioSx} checked={settings.busyNoteWaitMode === 'fixed'} onChange={() => update({ busyNoteWaitMode: 'fixed' })} />}
                label="Fixed"
              />
              <TextField
                size="small"
                type="number"
                disabled={settings.busyNoteWaitMode !== 'fixed'}
                value={settings.busyNoteFixedMinutes}
                onChange={(e) => update({ busyNoteFixedMinutes: Number(e.target.value) })}
                inputProps={{ min: BUSY_NOTE_MIN_MINUTES, max: BUSY_NOTE_MAX_MINUTES, 'aria-label': 'Busy note wait in minutes' }}
                sx={{ ...denseInputSx, width: 68, ml: 0.5 }}
              />
              <Typography sx={{ fontSize: 12.5, color: UI.muted }}>min</Typography>
            </Stack>
            {settings.busyNoteWaitMode === 'learn' && (
              <Typography sx={{ fontSize: 12, color: UI.muted }}>
                Follows the team's typical first reply over 14 days, kept between 15 and 30 min. Starts at 15.
              </Typography>
            )}
          </Stack>
        </SettingRow>
      </Box>

      {(errors.length > 0 || notice) && (
        <Stack gap={1} sx={{ px: 2.5, pb: 1.5 }}>
          {errors.length > 0 && (
            <Alert severity="error" onClose={() => setErrors([])} sx={{ fontSize: 12.5, py: 0 }}>
              {errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </Alert>
          )}
          {notice && (
            <Alert severity={notice.type} onClose={() => setNotice(null)} sx={{ fontSize: 12.5, py: 0 }}>
              {notice.text}
            </Alert>
          )}
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${UI.line}`, bgcolor: UI.canvas, borderRadius: '0 0 10px 10px', position: 'sticky', bottom: 0 }}
      >
        <Button variant="contained" disableElevation disabled={saving || !dirty} onClick={save} sx={primaryButtonSx}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        <Typography sx={{ fontSize: 12, color: UI.muted }}>{dirty ? 'Unsaved changes' : 'All changes saved'}</Typography>
      </Stack>
      <Dialog open={!!goLive} onClose={() => setGoLive(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Going Live: turn off the older automation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: UI.muted, mb: 1.25 }}>
            These older rules send the same messages as new flows you have switched on. Leave them on and customers get two messages.
          </Typography>
          {goLive?.map((row, idx) => (
            <FormControlLabel
              key={`${row.item.kind}-${row.item.id}`}
              sx={{ ...controlLabelSx, display: 'flex', mb: 0.75 }}
              control={
                <Checkbox
                  size="small"
                  sx={checkboxSx}
                  checked={row.checked}
                  onChange={(e) => setGoLive((prev) => prev && prev.map((r, i) => (i === idx ? { ...r, checked: e.target.checked } : r)))}
                />
              }
              label={
                <span>
                  <b>{row.item.name}</b> (sends to {row.item.recipient}). Replaced by {row.replacedBy.join(', ')}
                  {/broker/i.test(row.item.recipient) ? '. It messages brokers, so it stays on unless you tick it.' : '.'}
                </span>
              }
            />
          ))}
          <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.5 }}>
            You can turn any of them back on later in Journeys → Older automation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoLive(null)} sx={textButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving}
            onClick={async () => {
              const turnOff = (goLive || []).filter((r) => r.checked).map((r) => r.item);
              setGoLive(null);
              await persist(turnOff);
            }}
            sx={primaryButtonSx}
          >
            Go Live
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkingHoursSection;
